import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as actions from './DiscoverHistorics.ducks';
import * as searchactions from './containers/HistoricsSearchForm/HistoricsSearchForm.ducks';
import moment from 'moment-timezone';
import get from 'lodash/get';

import themeable from 'zignal-landscape/lib/themeable';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import { Dark } from 'themes';

import Banner from 'zignal-landscape/lib/Banner';
import TrendingModal from 'zignal-widgets/lib/TrendingModal';
import Modal from 'zignal-landscape/lib/Modal';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';
import ModalBody from 'zignal-landscape/lib/ModalBody';
import ModalFooter from 'zignal-landscape/lib/ModalFooter';
import Button from 'zignal-landscape/lib/Button';
import ButtonGroup from 'zignal-landscape/lib/ButtonGroup';
import MfeBulbIcon from 'zignal-landscape/lib/Icon/MfeBulbIcon';
import DateRangePicker from 'zignal-landscape/lib/DateRangePicker';

import HistoricsSearchForm from './containers/HistoricsSearchForm';
import HistoricsLineChart from './components/HistoricsLineChart';
import Stories from './components/Stories/Stories';
import SourceSelector from './components/SourceSelector/SourceSelector';
import MfeHeader from '../../components/MfeHeader/MfeHeader';
import RecentSearch from './components/RecentSearch';

import Loading from 'zignal-landscape/lib/Loading';
import { convertUrlParamTimesToDatePickerTimes } from '../../helpers/timeFormat';
import { createCustomPreset } from '../../helpers/timeFormat';
import { convertStrToAdditionalFilters, convertStrToSelectedLanguages, setAvailableLanguages } from './helpers';

import { SEARCH_TERM_COLORS } from './helpers';

const CUSTOM_PRESETS = [
  'last 30 days',
  'last 90 days',
  'last 6 months',
  'last 12 months',
  'last 2 years'
].map(createCustomPreset);

const BASIC_LANGUAGES = ['en', 'fr', 'es', 'de', 'pt', 'ru', 'zh', 'ar', 'fa', 'he'];

const getDateTimeTz = (value, timezone) => {
  if (value === 'now') {
    return moment.tz(timezone).format();
  }
  return moment.tz(value, 'YYYY-MM-DDTHH:mm:ss', timezone).format();
};

export const mapStateToProps = (
  (state) => ({
    fetchErrorMessage: state.DiscoverHistorics.Page.fetchErrorMessage,
    hasFetchErrors: state.DiscoverHistorics.Page.hasFetchErrors,
    isFetching: state.DiscoverHistorics.Page.isFetching,
    isFetchingTranslations: state.DiscoverHistorics.Page.isFetchingTranslations,
    hasMoreStories: state.DiscoverHistorics.Page.hasMoreStories,
    lastSearch: state.DiscoverHistorics.Page.lastSearch,
    timeSeriesCounts: state.DiscoverHistorics.Page.timeSeriesCounts,
    stories: state.DiscoverHistorics.Page.stories,
    intervalOptions: state.DiscoverHistorics.Page.intervalOptions,
    intervalSelectionIndex: state.DiscoverHistorics.Page.intervalSelectionIndex,
    timezone: state.AppUser.user.timezone,
    searchTerms: state.DiscoverHistorics.HistoricsSearchForm.searchTerms,
    filters: state.DiscoverHistorics.Page.filters,
    selectedSource: state.DiscoverHistorics.Page.selectedSource,
    loadingMoreStories: state.DiscoverHistorics.Page.loadingMoreStories,
    startDate: state.DiscoverHistorics.HistoricsSearchForm.startDate,
    endDate: state.DiscoverHistorics.HistoricsSearchForm.endDate,
    zqlArray: state.DiscoverHistorics.HistoricsSearchForm.zqlArray,
    lineChartBackDisabled: state.DiscoverHistorics.Page.lineChartBackDisabled,
    shouldScrollPage: state.DiscoverHistorics.Page.shouldScrollPage,
  })
);

export const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

const openLearnMoreLink = () => {
  document.dispatchEvent(new CustomEvent('external-link-click', {
    detail: { href: 'https://zignallabs.freshdesk.com/en/support/solutions/articles/24000093667-discover' }
  }));
};

export class DiscoverHistorics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      didSearch: false,
      searchTermIndex: -1,
      analysisModalIsOpen: false,
    };
    const availableLanguages = props.languages.map(lang => {
      return {
        label: lang.code.toUpperCase(),
        code: lang.code,
        name: lang.language,
        isSelected: false,
        isBasic: BASIC_LANGUAGES.includes(lang.code)
      };
    });
    // set the languages once on construction
    setAvailableLanguages(availableLanguages);
    this.availableLanguages = availableLanguages;
    this.onFetchSearchResults = this.onFetchSearchResults.bind(this);
    this.onFetchRecentSearchResults = this.onFetchRecentSearchResults.bind(this);
    this.setFilterSource = this.setFilterSource.bind(this);
    this.onShowTrendingModal = this.onShowTrendingModal.bind(this);
    this.onToggleAnalysisModal = this.onToggleAnalysisModal.bind(this);
    this.handleRemoveCssOverrides = this.handleRemoveCssOverrides.bind(this);
    this.onLoadMoreStories = this.onLoadMoreStories.bind(this);
  }

  componentDidUpdate(prevProps) {
    // scroll to loading icon
    if (this.props.shouldScrollPage && !prevProps.shouldScrollPage) {
      const element = document.getElementById('scrollAnchor');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  componentDidMount() {
    const { queryParams, setQueryParams, timezone, privacyMode = false, setPrivacyMode } = this.props;
    setPrivacyMode(privacyMode);
    const { q: searchTerms, start, end, f=[], l=[], k: enabledKeywords } = queryParams;
    if (searchTerms) {
      const times = convertUrlParamTimesToDatePickerTimes(start, end, timezone);
      const additionalFilters = convertStrToAdditionalFilters(f);
      const selectedLanguages = convertStrToSelectedLanguages(l);
      setQueryParams({ searchTerms, additionalFilters, selectedLanguages, ...times });
      this.onFetchRecentSearchResults(enabledKeywords);
    }

    // Add popstate event listener for browser back button
    window.addEventListener('popstate', this.handleRemoveCssOverrides);
  }

  componentWillUnmount() {
    this.props.clearSearchResults();

    // Remove classes and style overrides
    this.handleRemoveCssOverrides();
    // remove event listener for browser back button
    window.removeEventListener('popstate', this.handleRemoveCssOverrides);
  }

  handleRemoveCssOverrides() {
    window.$('#main-content').removeClass('discover-page');
    window.$('body').css('background-color', '#A0A6AE');
    window.$('.powered-by').css('display', 'initial');
  }

  onFetchSearchResults() {
    this.setState({ didSearch: true }, () => {
      this.props.fetchSearchResults(false);
    });
  }

  onFetchRecentSearchResults(payload) {
    this.setState({ didSearch: true }, () => {
      this.props.fetchRecentSearchResults(payload);
    });
  }

  onToggleAnalysisModal() {
    this.setState({ analysisModalIsOpen: !this.state.analysisModalIsOpen });
  }

  onShowTrendingModal(index) {
    if (index !== undefined && index >= 0 && index < this.props.zqlArray.length) {
      this.setState({ searchTermIndex: index });
    }
  }

  setFilterSource(source) {
    this.props.filterSource({ source, loadingMoreStories: true });
    // fetch the first page for the new source and replace the list
    this.props.fetchMoreStories({ reset: true });
  }

  onLoadMoreStories() {
    // InfiniteScroll passes a page number, the next page comes from the list length instead
    this.props.fetchMoreStories();
  }

  render() {
    const {
      styles,
      queryParams,
      timeSeriesCounts,
      clearFetchError,
      fetchErrorMessage,
      hasFetchErrors,
      fetchSearchInterval,
      fetchRecentSearches,
      fetchNewStories,
      clearRecentSearches,
      isFetching,
      isFetchingTranslations,
      lastSearch,
      intervalOptions,
      intervalSelectionIndex,
      selectInterval,
      searchTerms,
      filters,
      filterTerm,
      selectedSource,
      loadingMoreStories,
      startDate,
      endDate,
      zqlArray,
      timezone,
      lineChartBackDisabled,
      editDateRange,
      stories,
      hasMoreStories,
      isRestrictedUser,
      privacyMode = false
    } = this.props;

    const {
      didSearch,
      searchTermIndex,
      analysisModalIsOpen
    } = this.state;

    const startDateTime = getDateTimeTz(startDate, timezone);
    const endDateTime = getDateTimeTz(endDate, timezone);

    const errorMessageBody = typeof fetchErrorMessage === 'string'
      ? fetchErrorMessage
      : get(fetchErrorMessage, 'body', '');
    const errorMessage = typeof errorMessageBody === 'string' ? errorMessageBody.trim() : '';
    const errorTitle = get(fetchErrorMessage, 'title', '');
    const errorType = get(fetchErrorMessage, 'type', 'info');

    const errorMessageRow = hasFetchErrors ? (
      <div className={styles.discoverErrorMessage}>
        <div>
          <Banner
            bannerStyle="inline"
            bannerType={errorType}
            onClick={clearFetchError}
            title={errorTitle}
          >
            {errorMessage}
          </Banner>
        </div>
      </div>
    ) : null;

    return (
      <div className={styles.DiscoverHistorics} ref={(ref) => this.scrollParentRef = ref}>
        {errorMessageRow}
        <ThemeProvider theme={Dark}>
          <MfeHeader styles={styles} title="Discover">
            <div className={styles.headerContent}>
              <div className={styles.headerSection}>
                <button className={styles.learnMoreButton} onClick={openLearnMoreLink}>
                  <MfeBulbIcon />
                  &nbsp;&nbsp;Learn More
                </button>
              </div>
              <div className={styles.headerSection}>
                {!privacyMode && <RecentSearch
                  clearRecentSearches={clearRecentSearches}
                  fetchRecentSearchResults={this.onFetchRecentSearchResults}
                  fetchRecentSearches={fetchRecentSearches}
                  colors={SEARCH_TERM_COLORS}
                />}
                <div className={styles.headerDivider} />
                <div className={styles.datePicker}>
                  <DateRangePicker
                    onChange={editDateRange}
                    startEndDates={{
                      startDate,
                      endDate
                    }}
                    presets={CUSTOM_PRESETS}
                    size="normal"
                    inputDisplayFormat={'MM/DD/YYYY h:mma'}
                    timezone={timezone}
                    mfeMode
                  />
                </div>
                <button className={styles.searchButton} onClick={this.onFetchSearchResults}>
                  Search
                </button>
              </div>
            </div>
          </MfeHeader>
          <section className={styles.formWrapper}>
            <HistoricsSearchForm
              onSearch={this.onFetchSearchResults}
              saveAsProfile={this.props.saveAsProfile}
              onTranslateKeywords={this.props.fetchTranslations}
              colors={SEARCH_TERM_COLORS}
              queryParams={queryParams}
              startDate={startDate}
              isFetchingTranslations={isFetchingTranslations}
              availableLanguages={this.availableLanguages}
              isRestrictedUser={isRestrictedUser}
            />
          </section>
          <TrendingModal
            isOpen={searchTermIndex >= 0}
            isDiscover
            historicsSearchTerm={searchTermIndex >= 0 ? searchTerms[searchTermIndex] : ''}
            mayUserNavigate={false}
            apiArgs={{
              isHistoricsSearch: true,
              startTime: startDateTime,
              endTime: endDateTime,
              zql: searchTermIndex >= 0 ? [zqlArray[searchTermIndex]] : []
            }}
            onCloseCallback={() => this.setState({ searchTermIndex: -1 })}
          />
          <Modal
            isOpen={analysisModalIsOpen}
            size={'medium'}
            onEscapePress={this.onToggleAnalysisModal}
            onBackdropClick={this.onToggleAnalysisModal}
          >
            <ModalHeader
              headerStyle={'secondary'}
              onClose={this.onToggleAnalysisModal}
            >
              Analysis can't be performed
            </ModalHeader>
            <ModalBody>
              <h4>We're sorry,</h4>
              <p>
                The result count for this search exceeds the maximum limit of 1M results.
              </p>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup align="right">
                <Button buttonStyle="secondary" onClick={this.onToggleAnalysisModal}>Ok</Button>
              </ButtonGroup>
            </ModalFooter>
          </Modal>
          <div id="scrollAnchor">
            {didSearch ? (
              <HistoricsLineChart
                data={timeSeriesCounts}
                isFetching={isFetching}
                lastSearch={lastSearch}
                intervalOptions={intervalOptions}
                intervalSelectionIndex={intervalSelectionIndex}
                selectInterval={selectInterval}
                onSearch={fetchSearchInterval}
                colors={SEARCH_TERM_COLORS}
                searchTerms={searchTerms}
                onSearchNew={(pointStartDate, pointEndDate, backDisabled) =>
                  fetchNewStories({
                    startDate: pointStartDate,
                    endDate: pointEndDate,
                    lineChartBackDisabled: backDisabled
                  })
                }
                filters={filters}
                filterTerm={filterTerm}
                selectedSource="allSources" // graph should now always show all sources, no more filtering by source
                timezone={timezone}
                onShowTrendingModal={this.onShowTrendingModal}
                onToggleAnalysisModal={this.onToggleAnalysisModal}
                searchFormStartDate={startDate}
                searchFormEndDate={endDate}
                lineChartBackDisabled={lineChartBackDisabled}
              />
            ) : null}
          </div>
          {!isFetching && didSearch
            ? <div className={styles.discoverStoriesContainer}>
              <div className={styles.sourceRow}>
                <span className={styles.viewStoriesBy}>View Stories By:</span>
                <div className={styles.sourceSelector}>
                  <SourceSelector
                    data={timeSeriesCounts}
                    onSelectSource={this.setFilterSource}
                    selectedSource={selectedSource}
                  />
                </div>
              </div>
              <hr />
              {loadingMoreStories ?
                <div className={styles.loadingMoreStories}>
                  <Loading />
                </div> : null}
              <Stories
                stories={stories}
                onLoadMore={this.onLoadMoreStories}
                // no paging while a source change replaces the list
                hasMore={hasMoreStories && !loadingMoreStories}
                scrollParentRef={this.scrollParentRef}
              />
            </div>
            : null
          }
        </ThemeProvider>
      </div>
    );
  }
}

DiscoverHistorics.propTypes = {
  styles: PropTypes.object,
  queryParams: PropTypes.object,
  clearFetchError: PropTypes.func,
  fetchErrorMessage: PropTypes.object,
  hasFetchErrors: PropTypes.bool,
  fetchSearchResults: PropTypes.func.isRequired,
  fetchRecentSearchResults: PropTypes.func.isRequired,
  saveAsProfile: PropTypes.func.isRequired,
  fetchTranslations: PropTypes.func.isRequired,
  fetchSearchInterval: PropTypes.func.isRequired,
  fetchMoreStories: PropTypes.func.isRequired,
  hasMoreStories: PropTypes.bool.isRequired,
  fetchRecentSearches: PropTypes.func.isRequired,
  fetchNewStories: PropTypes.func,
  isFetching: PropTypes.bool.isRequired,
  isFetchingTranslations: PropTypes.bool.isRequired,
  lastSearch: PropTypes.array,
  timeSeriesCounts: PropTypes.array,
  stories: PropTypes.array,
  timezone: PropTypes.string,
  clearSearchResults: PropTypes.func,
  intervalOptions: PropTypes.array,
  intervalSelectionIndex: PropTypes.number,
  selectInterval: PropTypes.func,
  clearRecentSearches: PropTypes.func,
  searchTerms: PropTypes.array,
  filters: PropTypes.array.isRequired,
  filterTerm: PropTypes.func.isRequired,
  selectedSource: PropTypes.string,
  filterSource: PropTypes.func.isRequired,
  loadingMoreStories: PropTypes.bool.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  zqlArray: PropTypes.array,
  editDateRange: PropTypes.func.isRequired,
  lineChartBackDisabled: PropTypes.bool,
  isRestrictedUser: PropTypes.bool,
  privacyMode: PropTypes.bool,
  setPrivacyMode: PropTypes.func.isRequired
};

DiscoverHistorics.defaultProps = {
  timeSeriesCounts: [],
  stories: [],
  lastSearch: [],
  intervalOptions: [],
  intervalSelectionIndex: 1,
  selectedSource: null,
  zqlArray: [],
  isRestrictedUser: false
};

export default connect(mapStateToProps, {...actions, ...searchactions}, mergeProps)(themeable(DiscoverHistorics));
